/**
 * NotificationDropdown - Portaled dropdown for notifications
 *
 * Phase 3.1b - React Query as single source of truth
 *
 * Features:
 * - Portaled to body with z-40 (dropdown layer)
 * - Collision handling (clamp to viewport)
 * - Position listeners (scroll, resize, sidebar-collapse-change)
 * - Shows last 10 notifications with "View all" link
 * - React Query caches notifications list (no local useState)
 */

import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import type { RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Bell, User, Award, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import {
  useNotificationsList,
  type Notification,
} from '../hooks/useNotificationsQuery';
import { queryKeys } from '../lib/queryKeys';
import { Z_INDEX } from '../utils/z-index';
import { formatDistanceToNow } from '../utils/dateUtils';
import { NotificationSkeleton } from './Skeleton';

interface NotificationDropdownProps {
  anchorRef: RefObject<HTMLButtonElement | null>;
  onClose: () => void;
}

export function NotificationDropdown({ anchorRef, onClose }: NotificationDropdownProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { markAsRead, markAllAsRead } = useNotifications();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, right: 0 });
  // Phase 5F: Gate portal render until position is computed to prevent flash at (0,0)
  const [positionReady, setPositionReady] = useState(false);

  // React Query hook - single source of truth for notifications list
  const { notifications, isLoading } = useNotificationsList({ limit: 10 });

  // Phase 5F: Only show "Loading..." when no cached data; use isFetching for background refresh
  const showLoadingSkeleton = isLoading && notifications.length === 0;

  // Calculate position relative to anchor
  const updatePosition = useCallback(() => {
    if (!anchorRef.current) return;

    const rect = anchorRef.current.getBoundingClientRect();
    const dropdownWidth = 360;
    const dropdownMaxHeight = 400;

    // Position below the anchor, aligned to the right
    let top = rect.bottom + 8;
    let right = window.innerWidth - rect.right;

    // Collision detection - clamp to viewport
    // Vertical: if dropdown would go below viewport, position above
    if (top + dropdownMaxHeight > window.innerHeight - 16) {
      top = Math.max(16, rect.top - dropdownMaxHeight - 8);
    }

    // Horizontal: ensure dropdown stays within viewport
    if (right + dropdownWidth > window.innerWidth - 16) {
      right = window.innerWidth - dropdownWidth - 16;
    }
    if (right < 16) {
      right = 16;
    }

    setPosition({ top, right });
    setPositionReady(true);
  }, [anchorRef]);

  // Phase 5F: Use useLayoutEffect for initial position to prevent flash at (0,0)
  // This runs BEFORE paint, so the dropdown renders at correct position immediately
  useLayoutEffect(() => {
    updatePosition();
  }, [updatePosition]);

  // Invalidate queries on mount (FIX 4: deterministic refetch on open)
  useEffect(() => {
    // FIX 4: Invalidate both unread count and list to ensure fresh data
    if (user?.id) {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount(user.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.list(user.id, { limit: 10, cursor: null, type: null }) });
    }
  }, [user?.id, queryClient]);

  // Position listeners
  useEffect(() => {
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('sidebar-collapse-change', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('sidebar-collapse-change', updatePosition);
    };
  }, [updatePosition]);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        anchorRef.current &&
        !anchorRef.current.contains(target)
      ) {
        onClose();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [anchorRef, onClose]);

  // Close on Escape key
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Handle notification click - mutation updates React Query cache
  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read (mutation updates cache optimistically)
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }

    // Navigate based on notification type (system notifications only - inbox types handled in /inbox)
    if (notification.type === 'new_follower' && notification.from_user?.username) {
      navigate(`/u/${notification.from_user.username}`);
      onClose();
    }
    // milestone notifications don't navigate anywhere
  };

  // Handle mark all as read - mutation updates React Query cache
  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  // Get icon for notification type (system notifications only)
  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'new_follower':
        return <User size={16} className="text-teal-400" />;
      case 'milestone':
        return <Award size={16} className="text-purple-400" />;
      default:
        return <Bell size={16} className="text-slate-400" />;
    }
  };

  const hasUnread = notifications.some((n) => !n.is_read);

  // Phase 5F: Don't render portal until position is computed to prevent flash at (0,0)
  if (!positionReady) {
    return null;
  }

  return createPortal(
    <div
      ref={dropdownRef}
      className="fixed w-[360px] max-h-[400px] bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-lg shadow-xl shadow-black/40 overflow-hidden flex flex-col"
      style={{
        top: position.top,
        right: position.right,
        zIndex: Z_INDEX.dropdown,
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.08]">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-white">Notifications</h3>
        </div>
        {hasUnread && (
          <button
            onClick={handleMarkAllAsRead}
            className="text-xs text-teal-400 hover:text-teal-300 transition-colors flex items-center gap-1"
          >
            <Check size={12} />
            Mark all read
          </button>
        )}
      </div>

      {/* Notifications list */}
      <div className="flex-1 overflow-y-auto">
        {/* Phase 5F: Only show skeleton when no cached data; otherwise show cached data */}
        {showLoadingSkeleton ? (
          <div>
            {[...Array(4)].map((_, i) => (
              <NotificationSkeleton key={i} />
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-8 text-center">
            <Bell size={32} className="mx-auto mb-2 text-slate-600" />
            <p className="text-sm text-slate-400">No notifications yet</p>
          </div>
        ) : (
          <ul>
            {notifications.map((notification) => (
              <li key={notification.id}>
                <button
                  onClick={() => handleNotificationClick(notification)}
                  className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-white/[0.04] transition-colors ${
                    !notification.is_read ? 'bg-teal-500/5' : ''
                  }`}
                >
                  {/* Avatar or icon */}
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center overflow-hidden">
                    {notification.from_user?.avatar_url ? (
                      <img
                        src={notification.from_user.avatar_url}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      getNotificationIcon(notification.type)
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-200">
                      {notification.from_user && (
                        <span className="font-medium">
                          {notification.from_user.display_name || notification.from_user.username}
                        </span>
                      )}{' '}
                      {notification.body}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {formatDistanceToNow(notification.created_at)}
                    </p>
                  </div>

                  {/* Unread indicator */}
                  {!notification.is_read && (
                    <div className="flex-shrink-0 w-2 h-2 mt-2 bg-teal-400 rounded-full" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Footer - View all link */}
      <Link
        to="/notifications"
        onClick={onClose}
        className="block px-4 py-3 text-center text-sm text-teal-400 hover:text-teal-300 hover:bg-white/[0.04] border-t border-white/[0.08] transition-colors"
      >
        View all notifications
      </Link>
    </div>,
    document.body
  );
}
